import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import IconDown from 'design-system/icons/IconDown';
import BlurAmountView from 'design-system/BlurAmountView';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { ChartXLabel, ChartYLabel } from './Chart/ChartLabels';
import { formatAmount, getRisingFalling } from '../utils';
import usePriceChangeSharedValue from '../hooks/usePriceChangeSharedValue';
import useFormatTimestampWorklet from '../hooks/useFormatTimestampWorklet';
import useFormatPriceChangeForStockWorklet from '../hooks/useFormatPriceChangeForStockWorklet';
import useFormatPriceChangeForPortfolioWorklet from '../hooks/useFormatPriceChangeForPortfolioWorklet';
import usePriceChangeArrowStyles from '../hooks/usePriceChangeArrowStyles';
import usePriceChangeAnimatedStyle, { FONT_SIZE } from '../hooks/usePriceChangeAnimatedStyle';
const PriceChange = ({ percent, amount, withAmount, contextValue, forStock, withTime, period, scrambled, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const forStockShared = useSharedValue(Boolean(forStock));
    const priceChangeShared = usePriceChangeSharedValue(amount, percent, withAmount);
    const { originalXShared, originalDataShared } = contextValue || {};
    const formatTimestampWorklet = useFormatTimestampWorklet(originalDataShared, forStock ? 'stock' : 'portfolio', withTime, period);
    const formatPriceChangeForStock = useFormatPriceChangeForStockWorklet(priceChangeShared, originalDataShared);
    const formatPriceChangeForPortfolio = useFormatPriceChangeForPortfolioWorklet(priceChangeShared);
    const amountShared = useSharedValue(amount.value);
    amountShared.value = amount.value;
    const priceChangeColorStyle = usePriceChangeAnimatedStyle(forStockShared, originalDataShared, originalXShared, amountShared);
    const [upAnimatedStyle, downAnimatedStyle, neutralAnimatedStyle] = usePriceChangeArrowStyles(amountShared, forStockShared, originalDataShared, originalXShared);
    const priceChange = useMemo(() => {
        if (contextValue) {
            if (forStock) {
                return (<ChartYLabel forStock style={priceChangeColorStyle} format={formatPriceChangeForStock} contextValue={contextValue} keyToUse="closePrices"/>);
            }
            return (<ChartYLabel forPortfolio style={priceChangeColorStyle} format={formatPriceChangeForPortfolio} contextValue={contextValue} keysToUse={['profitLossPct', 'profitLoss']}/>);
        }
        const [rising, falling] = getRisingFalling(percent, amount, withAmount);
        return (<BlurAmountView enabled={scrambled}>
        <Text style={[styles.priceChange, rising && styles.green, falling && styles.red]}>
          {withAmount
                ? `${Math.abs(percent).toFixed(2)}% (${formatAmount({
                    value: Math.abs(amount.value),
                    currency: amount.currency,
                })})`
                : `${Math.abs(percent).toFixed(2)}%`}
        </Text>
      </BlurAmountView>);
    }, [
        contextValue,
        percent,
        amount,
        withAmount,
        scrambled,
        styles.priceChange,
        styles.green,
        styles.red,
        forStock,
        priceChangeColorStyle,
        formatPriceChangeForPortfolio,
        formatPriceChangeForStock,
    ]);
    const time = useMemo(() => {
        if (contextValue) {
            if (forStock) {
                return (<ChartXLabel forStock style={styles.time} format={formatTimestampWorklet} contextValue={contextValue} keyToUse="timestamps"/>);
            }
            return (<ChartXLabel forPortfolio style={styles.time} format={formatTimestampWorklet} contextValue={contextValue} keyToUse="timestamp"/>);
        }
        return null;
    }, [styles, contextValue, formatTimestampWorklet, forStock]);
    const arrow = useMemo(() => {
        if (contextValue) {
            return (<View style={styles.changeIndicatorContainer}>
          <Animated.View style={upAnimatedStyle}>
            <View style={styles.upIcon}>
              <IconDown color={colors.text.positive}/>
            </View>
          </Animated.View>
          <Animated.View style={downAnimatedStyle}>
            <IconDown color={colors.text.negative}/>
          </Animated.View>
          <Animated.View style={neutralAnimatedStyle}>
            <View style={styles.neutralIndicator}/>
          </Animated.View>
        </View>);
        }
        const [rising, falling] = getRisingFalling(percent, amount, withAmount);
        if (rising) {
            return (<View style={[styles.changeIndicatorContainer, styles.upIcon]}>
          <IconDown color={colors.text.positive}/>
        </View>);
        }
        if (falling) {
            return (<View style={styles.changeIndicatorContainer}>
          <IconDown color={colors.text.negative}/>
        </View>);
        }
        return (<View style={styles.changeIndicatorContainer}>
        <View style={styles.neutralIndicator}/>
      </View>);
    }, [
        colors,
        styles,
        contextValue,
        upAnimatedStyle,
        downAnimatedStyle,
        neutralAnimatedStyle,
        percent,
        amount,
        withAmount,
    ]);
    return (<View style={styles.row}>
      {arrow}
      {priceChange}
      {time}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    green: {
        color: colors.text.positive,
    },
    red: {
        color: colors.text.negative,
    },
    upIcon: {
        transform: [{ rotate: '180deg' }],
    },
    changeIndicatorContainer: {
        marginRight: 4,
        width: rem(7),
        height: rem(7),
        alignItems: 'center',
        justifyContent: 'center',
    },
    neutralIndicator: {
        width: rem(4),
        height: rem(4),
        borderRadius: rem(2),
        backgroundColor: colors.text.secondary,
    },
    priceChange: {
        fontFamily: 'Nunito-Light',
        fontSize: FONT_SIZE,
        color: colors.text.secondary,
    },
    time: {
        fontFamily: 'Montserrat',
        fontWeight: '300',
        fontSize: FONT_SIZE,
        color: colors.text.secondary,
        marginLeft: 8,
        padding: 0,
    },
}));
export default PriceChange;
